import React from 'react';
import { View } from 'react-native';

import { HStack, Text, VStack, theme } from '@squire/ui';

import { ApplePayIcon } from '@/components/_icons';
import { CardBrandIcon } from '@/components/_icons/CardBrandIcon';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';

import { styles } from './styles';
import type { PaymentStatusProps } from './types';
import {
  isBnplPayment,
  isGiftCardPaymentMethod,
  isPaymentCaptured,
} from './utils';

const PaymentStatus: React.FC<PaymentStatusProps> = ({
  paymentMethod,
  paymentState,
  shouldShowDisclaimer,
  cardInfo,
  isCanceled = false,
  shopName,
  isApplePay: isApplePayProp,
  captured,
  capturedAt,
}) => {
  const { t } = useScreenTranslations('ViewReservationPaymentStatus');

  // BNPL is a card charge captured at booking, so it looks like a paid card
  // (succeeded + captured). The customer still owes the installments, so it
  // must read as pending. Identified by cardInfo.last4 === 'BNPL'.
  const isBnpl = isBnplPayment(cardInfo);

  // The customer-sale API reports a captured card payment as state
  // 'succeeded' (there is no 'processed' payment state — that's the sale
  // order's state). 'succeeded' only means PAID once the charge is captured;
  // an authorized-but-not-captured charge (Stripe requires_capture) is still
  // succeeded but should read as pending.
  const isCaptured = isPaymentCaptured(captured, capturedAt);

  const isGiftCard = isGiftCardPaymentMethod(paymentMethod);

  const isProcessed =
    !isBnpl &&
    (paymentState === 'processed' ||
      (paymentState === 'succeeded' && (isGiftCard || isCaptured)));

  const isPendingPayment =
    paymentState === 'pending' ||
    paymentState === 'processing' ||
    paymentState === 'authorized' ||
    (paymentState === 'succeeded' && !isGiftCard && (isBnpl || !isCaptured));

  // For a canceled appointment we cannot tell a kept cancellation fee apart
  // from a captured-then-refunded charge: both come back as succeeded +
  // captured, and the customer-sale response does not serialize refunds. Only
  // treat it as charged when the sale order itself reports 'processed', to
  // avoid falsely telling a refunded customer they were charged.
  const wasCharged = isCanceled && paymentState === 'processed';

  const isApplePay =
    isApplePayProp ||
    paymentMethod === 'apple_pay' ||
    paymentMethod === 'Apple Pay' ||
    paymentMethod === 'apple' ||
    cardInfo?.tokenizationMethod === 'apple_pay';

  const isCard = paymentMethod === 'card' && cardInfo;
  const cardBrand = cardInfo?.brand?.toLowerCase();

  const getCardDisplayText = () => {
    if (cardInfo?.last4) {
      return `•••• ${cardInfo.last4}`;
    }
    if (paymentMethod === 'card') {
      return t('creditCard');
    }
    return paymentMethod;
  };

  const normalisedMethod = paymentMethod?.toLowerCase().replace(/[\s_-]/g, '');
  const isPayInPerson = normalisedMethod === 'payinperson';

  const renderCardIcon = () => {
    if (isPayInPerson) return <CardBrandIcon brand="payInPerson" />;
    if (isGiftCard) return <CardBrandIcon brand="giftCard" />;
    if (isApplePay) return <ApplePayIcon size={32} />;
    if (isCard && cardBrand) return <CardBrandIcon brand={cardBrand} />;
    if (normalisedMethod === 'cashapp')
      return <CardBrandIcon brand="cashapp" />;
    if (normalisedMethod === 'bnpl' || normalisedMethod === 'buynowpaylater')
      return <CardBrandIcon brand="bnpl" />;
    if (normalisedMethod === 'googlepay')
      return <CardBrandIcon brand="googlepay" />;
    return null;
  };

  return (
    <VStack space={theme.spacing[4]}>
      <HStack style={styles.spaceBetween}>
        <HStack space={theme.spacing[3]} style={styles.rowCentered}>
          {renderCardIcon()}
          <Text variant="lStrong" color="textPrimary">
            {isApplePay
              ? t('applePay')
              : isPayInPerson
                ? t('payInPerson')
                : isGiftCard
                  ? t('giftCard')
                  : getCardDisplayText()}
          </Text>
        </HStack>
        {isCanceled ? (
          <View style={wasCharged ? styles.paidBadge : styles.canceledBadge}>
            <Text
              style={
                wasCharged ? styles.paidBadgeText : styles.canceledBadgeText
              }
            >
              {wasCharged ? t('paid') : t('canceled')}
            </Text>
          </View>
        ) : isProcessed ? (
          <View style={styles.paidBadge}>
            <Text style={styles.paidBadgeText}>{t('paid')}</Text>
          </View>
        ) : (
          isPendingPayment && (
            <View style={styles.pendingBadge}>
              <Text style={styles.pendingBadgeText}>{t('pending')}</Text>
            </View>
          )
        )}
      </HStack>
      {isCanceled ? (
        <Text variant="s" color="textSecondary">
          {wasCharged
            ? t('canceledCharged', { shopName: shopName || '' })
            : t('canceledNotCharged')}
        </Text>
      ) : (
        shouldShowDisclaimer && (
          <Text variant="s" color="textSecondary">
            {t('disclaimer')}
          </Text>
        )
      )}
    </VStack>
  );
};

PaymentStatus.displayName = 'PaymentStatus';
export { PaymentStatus };
